/**
 * Where the service map draws its nodes and arrows (design "Traces: service map"), computed here so it can be tested
 * without a browser. Columns go left to right by the longest path from the entry nodes (the Browser and anything nobody
 * calls first; an arrow that closes a cycle does not count). A Kafka topic sits in its producer's column, between the
 * producer and its consumers (the arrows in and out of a topic do not move one column on); a datastore or an outside
 * host goes one column to the right of its callers. In a column the rows follow their callers (a node lines up with
 * the average height of who calls it, after any caller in its own column), spread evenly around the middle.
 * Arrows go from border to border, a pair A→B / B→A side by side; their thickness grows with the calls.
 * Each arrow's label sits in the middle of the arrow's visible part (between the two boxes), beside it when that part
 * is too short, then is nudged until it covers no box and no other label (placeLabels).
 */
import { edgeErrors, edgeLabel, edgeReason } from './tracesModel';

export const NODE_SIZES = {
  browser: { w: 190, h: 60 },
  service: { w: 164, h: 64 },
  topic: { w: 164, h: 60 },
  datastore: { w: 132, h: 60 },
  external: { w: 150, h: 60 },
};
const SLOT_W = 164;
const PAD = 24;
const ROW_GAP = 46;
const MIN_COL_GAP = 48;
const MIN_HEIGHT = 340;
const FOOT = 34; // room for the footnote under the drawing

// A frontend in the browser has a third line ("in the browser").
const sizeOf = (node) => (node?.inBrowser ? { w: NODE_SIZES.service.w, h: 78 } : NODE_SIZES[node?.kind] || NODE_SIZES.service);

/**
 * Which column each node goes in, the arrows that close a cycle (`back`), and a topological order of the rest.
 * Returns `{ column: Map<id, n>, back: Set<edgeId>, order: id[] }`.
 */
export function mapColumns(nodes = [], edges = []) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const valid = edges.filter((e) => byId.has(e.from) && byId.has(e.to) && e.from !== e.to);
  const out = new Map(nodes.map((n) => [n.id, []]));
  const incoming = new Map(nodes.map((n) => [n.id, 0]));
  valid.forEach((e) => { out.get(e.from).push(e); incoming.set(e.to, incoming.get(e.to) + 1); });
  out.forEach((list) => list.sort((a, b) => (b.calls || 0) - (a.calls || 0)));

  // The entries: the frontends in the browser and the Visitors first, then whatever nobody calls; with neither (all
  // in a cycle) the first node.
  const first = (n) => n.kind === 'browser' || n.inBrowser;
  const entries = [
    ...nodes.filter(first),
    ...nodes.filter((n) => !first(n) && incoming.get(n.id) === 0),
  ];
  if (!entries.length && nodes.length) { entries.push(nodes[0]); }

  // Depth-first from the entries (then from anything not reached): an arrow back to a node still open is a cycle.
  const state = new Map();
  const back = new Set();
  const post = [];
  const visit = (id) => {
    state.set(id, 'open');
    out.get(id).forEach((e) => {
      const s = state.get(e.to);
      if (s === 'open') { back.add(e.id); return; }
      if (!s) { visit(e.to); }
    });
    state.set(id, 'done');
    post.push(id);
  };
  entries.forEach((n) => { if (!state.get(n.id)) { visit(n.id); } });
  nodes.forEach((n) => { if (!state.get(n.id)) { visit(n.id); } });
  const order = post.reverse();

  // The longest path, in topological order; through a topic no column is added.
  const column = new Map(nodes.map((n) => [n.id, 0]));
  order.forEach((id) => {
    out.get(id).forEach((e) => {
      if (back.has(e.id)) { return; }
      const step = byId.get(e.from).kind === 'topic' || byId.get(e.to).kind === 'topic' ? 0 : 1;
      column.set(e.to, Math.max(column.get(e.to), column.get(id) + step));
    });
  });
  return { column, back, order };
}

/** The point where the line from a box's centre towards (dx, dy) leaves the box. */
function border(cx, cy, w, h, dx, dy) {
  const tx = dx ? (w / 2) / Math.abs(dx) : Infinity;
  const ty = dy ? (h / 2) / Math.abs(dy) : Infinity;
  const t = Math.min(tx, ty);
  return { x: cx + dx * t, y: cy + dy * t };
}

/** The stroke width of an arrow: 1.2 px for the fewest calls, up to 4.5 px for the most. */
export function strokeWidth(calls, maxCalls) {
  if (!maxCalls || !calls) { return 1.2; }
  return Math.round((1.2 + 3.3 * Math.sqrt(Math.min(1, calls / maxCalls))) * 10) / 10;
}

/**
 * The whole drawing for a box `width` wide (at least as wide as the columns need):
 * `{ width, height, nodes: [{ ...node, x, y, w, h, column }], edges: [{ ...edge, x1, y1, x2, y2, lx, ly, stroke, back }] }`.
 */
export function layoutMap(nodes = [], edges = [], { width = 880 } = {}) {
  if (!nodes.length) { return { width, height: MIN_HEIGHT, nodes: [], edges: [] }; }
  const { column, back, order } = mapColumns(nodes, edges);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const rank = new Map(order.map((id, i) => [id, i]));
  const columns = Math.max(...column.values()) + 1;
  const step = columns > 1 ? Math.max(SLOT_W + MIN_COL_GAP, (width - 2 * PAD - SLOT_W) / (columns - 1)) : 0;
  const drawWidth = Math.max(width, 2 * PAD + SLOT_W + (columns - 1) * step);

  // Each column's stack height decides the drawing's height.
  const groups = Array.from({ length: columns }, () => []);
  nodes.forEach((n) => groups[column.get(n.id)].push(n));
  const stackHeight = (list) => list.reduce((sum, n) => sum + sizeOf(n).h, 0) + Math.max(0, list.length - 1) * ROW_GAP;
  const height = Math.max(MIN_HEIGHT, Math.max(...groups.map(stackHeight)) + 2 * PAD + FOOT);
  const middle = (height - FOOT) / 2;

  const preds = new Map(nodes.map((n) => [n.id, []]));
  edges.forEach((e) => { if (!back.has(e.id) && byId.has(e.from) && byId.has(e.to) && e.from !== e.to) { preds.get(e.to).push(e.from); } });

  const placed = new Map();
  const key = new Map();
  groups.forEach((list, c) => {
    // In topological order, so a caller in the same column has its key first.
    const ordered = [...list].sort((a, b) => rank.get(a.id) - rank.get(b.id));
    ordered.forEach((n) => {
      const before = preds.get(n.id).filter((p) => placed.has(p));
      const same = preds.get(n.id).filter((p) => column.get(p) === c && key.has(p));
      const fromLeft = before.length ? before.reduce((s, p) => s + placed.get(p).cy, 0) / before.length : middle;
      const afterSame = same.length ? Math.max(...same.map((p) => key.get(p))) + 0.01 : -Infinity;
      key.set(n.id, Math.max(fromLeft, afterSame));
    });
    ordered.sort((a, b) => (key.get(a.id) - key.get(b.id)) || (rank.get(a.id) - rank.get(b.id)));
    let y = middle - stackHeight(ordered) / 2;
    ordered.forEach((n) => {
      const { w, h } = sizeOf(n);
      const x = PAD + c * step + (SLOT_W - w) / 2;
      placed.set(n.id, { x, y, w, h, cx: x + w / 2, cy: y + h / 2, column: c });
      y += h + ROW_GAP;
    });
  });

  const maxCalls = Math.max(0, ...edges.map((e) => e.calls || 0));
  const pairs = new Set(edges.map((e) => `${e.from}>${e.to}`));
  const laidEdges = edges
    .filter((e) => placed.has(e.from) && placed.has(e.to) && e.from !== e.to)
    .map((e) => {
      const a = placed.get(e.from);
      const b = placed.get(e.to);
      let dx = b.cx - a.cx;
      let dy = b.cy - a.cy;
      const len = Math.hypot(dx, dy) || 1;
      dx /= len; dy /= len;
      // A pair both ways: each arrow 7 px to its own side.
      const shift = pairs.has(`${e.to}>${e.from}`) ? 7 : 0;
      const sx = -dy * shift;
      const sy = dx * shift;
      const start = border(a.cx, a.cy, a.w, a.h, dx, dy);
      const end = border(b.cx, b.cy, b.w, b.h, -dx, -dy);
      const x1 = start.x + sx; const y1 = start.y + sy;
      const x2 = end.x + sx - dx * 2; const y2 = end.y + sy - dy * 2;
      return { ...e, x1, y1, x2, y2, lx: (x1 + x2) / 2, ly: (y1 + y2) / 2, stroke: strokeWidth(e.calls, maxCalls), back: back.has(e.id) };
    });

  const laidNodes = nodes.map((n) => ({ ...n, ...placed.get(n.id) }));
  return {
    width: drawWidth,
    height,
    nodes: laidNodes,
    edges: placeLabels(laidEdges, laidNodes, { width: drawWidth, height }),
  };
}

// ---- Arrow labels ----

const CHAR_W = 6.6; // 11.5px IBM Plex Sans, a little generous
const LABEL_PAD_X = 14;
const LINE_H = 17;
const GAP = 4; // between a label and a box, another label or its arrow

/** A label's size, from its text: `{ w, h }` (two lines when it says "N errors", three with a reason, cut at 40). */
export function labelSize(edge) {
  const reason = edgeReason(edge);
  const lines = [edgeLabel(edge), edgeErrors(edge), reason ? reason.slice(0, 40) : null].filter(Boolean);
  const longest = Math.max(...lines.map((l) => l.length));
  return { w: Math.ceil(longest * CHAR_W + LABEL_PAD_X), h: lines.length * LINE_H + 2 };
}

/** Whether two rects `{ x, y, w, h }` overlap, `gap` px apart counting as overlapping. */
export function rectsOverlap(a, b, gap = 0) {
  return a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;
}

/**
 * Where each arrow's label goes, as its centre (`lx`, `ly`) and its rect (`label: { x, y, w, h }`):
 * - in the middle of the visible part of the arrow (`x1,y1 → x2,y2`, already clipped at the two boxes);
 * - when that part is shorter than the label along it: beside the arrow (right of a mostly vertical one, above a
 *   mostly horizontal one);
 * - then, busiest arrow first, nudged in growing steps (down, right, up, left, …) until it overlaps no box and no label
 *   placed before it, inside the drawing; after `maxTries` it stays where it fits least badly (its first place).
 */
export function placeLabels(edges, nodes, { width = Infinity, height = Infinity, size = labelSize, maxTries = 160, step = 6 } = {}) {
  const boxes = nodes.filter((n) => Number.isFinite(n.x)).map((n) => ({ x: n.x, y: n.y, w: n.w, h: n.h }));
  const placedRects = [];
  const order = [...edges.keys()].sort((a, b) => (edges[b].calls || 0) - (edges[a].calls || 0));
  const result = [...edges];
  const inside = (r) => r.x >= 0 && r.y >= 0 && r.x + r.w <= width && r.y + r.h <= height;
  const free = (r) => inside(r) && !boxes.some((b) => rectsOverlap(r, b, GAP)) && !placedRects.some((p) => rectsOverlap(r, p, GAP));
  order.forEach((i) => {
    const e = edges[i];
    const { w, h } = size(e);
    const mx = (e.x1 + e.x2) / 2;
    const my = (e.y1 + e.y2) / 2;
    const dx = e.x2 - e.x1;
    const dy = e.y2 - e.y1;
    const visible = Math.hypot(dx, dy);
    const vertical = Math.abs(dy) >= Math.abs(dx);
    const along = vertical ? h : w;
    let cx = mx;
    let cy = my;
    let beside = false;
    if (visible < along + 2 * GAP) {
      beside = true;
      const half = (e.stroke || 1.5) / 2 + GAP;
      if (vertical) { cx = mx + half + w / 2; } else { cy = my - half - h / 2; }
    }
    const rectAt = (x, y) => ({ x: x - w / 2, y: y - h / 2, w, h });
    let best = rectAt(cx, cy);
    if (!free(best)) {
      const dirs = [[0, 1], [1, 0], [0, -1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];
      for (let t = 1; t <= maxTries; t += 1) {
        const [ux, uy] = dirs[(t - 1) % dirs.length];
        const reach = Math.ceil(t / dirs.length) * step;
        const candidate = rectAt(cx + ux * reach, cy + uy * reach);
        if (free(candidate)) { best = candidate; break; }
      }
    }
    placedRects.push(best);
    result[i] = { ...e, lx: best.x + w / 2, ly: best.y + h / 2, label: best, labelBeside: beside };
  });
  return result;
}
